import React, { useEffect, useState } from 'react';
import {
  Box,
  Button,
  Container,
  Divider,
  HStack,
  Icon,
  IconButton,
  Link,
  SimpleGrid,
  Stack,
  Text,
  useToast,
  VStack,
} from '@chakra-ui/react';
import { ArrowForwardIcon, CopyIcon, EmailIcon } from '@chakra-ui/icons';
import { Link as RouterLink } from 'react-router-dom';

const serviceLinks = [
  { label: 'Real Estate Media', to: '/services/real-estate' },
  { label: 'Business Media', to: '/services/business-media' },
  { label: 'Business Web Apps', to: '/services/web-development' },
];

const exploreLinks = [
  { label: 'Home', to: '/' },
  { label: 'Portfolio', to: '/portfolio' },
  { label: 'iLand Studio Platform', to: '/platform' },
  { label: 'Book a Shoot', to: '/book' },
  { label: 'FAQ', to: '/faq' },
];

type AppVersion = {
  builtAt?: string;
};

const formatBuildDate = (builtAt?: string) => {
  if (!builtAt) return '';

  const date = new Date(builtAt);
  if (Number.isNaN(date.getTime())) return '';

  return new Intl.DateTimeFormat(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  }).format(date);
};

const YouTubeMark: React.FC = () => (
  <Icon viewBox="0 0 24 24" boxSize={4} color="currentColor" flexShrink={0}>
    <path
      fill="currentColor"
      d="M21.6 7.2s-.2-1.5-.8-2.1c-.8-.8-1.7-.8-2.1-.9C15.8 4 12 4 12 4s-3.8 0-6.7.2c-.4.1-1.3.1-2.1.9-.6.6-.8 2.1-.8 2.1S2.2 9 2.2 10.8v1.7c0 1.8.2 3.6.2 3.6s.2 1.5.8 2.1c.8.8 1.8.8 2.3.9 1.7.2 6.5.2 6.5.2s3.8 0 6.7-.2c.4-.1 1.3-.1 2.1-.9.6-.6.8-2.1.8-2.1s.2-1.8.2-3.6v-1.7c0-1.8-.2-3.6-.2-3.6ZM10.1 14.8V8.6l5.8 3.1-5.8 3.1Z"
    />
  </Icon>
);

const FooterLink: React.FC<{ to: string; children: React.ReactNode }> = ({ to, children }) => (
  <Link
    as={RouterLink}
    to={to}
    color="whiteAlpha.800"
    fontSize="sm"
    fontWeight="600"
    display="inline-flex"
    alignItems="center"
    gap={2}
    _hover={{ color: 'orange.300', textDecoration: 'none', transform: 'translateX(2px)' }}
    transition="color 0.2s ease, transform 0.2s ease"
  >
    <Box w="4px" h="4px" borderRadius="full" bg="teal.300" />
    {children}
  </Link>
);

const Footer: React.FC = () => {
  const toast = useToast();
  const [buildDate, setBuildDate] = useState('');

  useEffect(() => {
    let cancelled = false;

    fetch(`/app-version.json?t=${Date.now()}`, { cache: 'no-store' })
      .then((response) => (response.ok ? response.json() as Promise<AppVersion> : null))
      .then((version) => {
        if (!version || cancelled) return;
        const formatted = formatBuildDate(version.builtAt);
        if (formatted) setBuildDate(formatted);
      })
      .catch(() => undefined);

    return () => {
      cancelled = true;
    };
  }, []);

  const copyEmail = () => {
    navigator.clipboard?.writeText('contact@iland.studio');
    toast({
      title: 'Email copied',
      description: 'contact@iland.studio',
      status: 'success',
      duration: 1800,
      isClosable: true,
    });
  };

  return (
    <Box as="footer" bg="#0f1622" color="white" borderTop="1px solid" borderColor="whiteAlpha.200" position="relative" overflow="hidden">
      <Box
        position="absolute"
        inset="0"
        bg="radial-gradient(circle at 12% 15%, rgba(79,209,197,0.10), transparent 30%), radial-gradient(circle at 88% 0%, rgba(246,173,85,0.08), transparent 28%)"
        pointerEvents="none"
      />

      <Container maxW="container.xl" px={{ base: 4, md: 6 }} py={{ base: 7, md: 9 }} position="relative">
        <SimpleGrid columns={{ base: 1, sm: 2, lg: 12 }} spacing={{ base: 7, lg: 8 }}>
          <VStack align="flex-start" spacing={3} gridColumn={{ lg: 'span 4' }}>
            <Box>
              <Text fontWeight="900" fontSize="1.6rem" lineHeight="1">
                <Text as="span" color="orange.300">i</Text>Land Studio
              </Text>
              <Text mt={2} color="teal.300" fontSize="0.7rem" fontWeight="800" letterSpacing="0.17em" textTransform="uppercase">
                Ottawa Canada Media &amp; Web Studio
              </Text>
            </Box>
            <Text color="whiteAlpha.700" fontSize="sm" lineHeight="1.6" maxW="390px">
              Real estate media, business visuals, practical web apps, and the upcoming
              iLand Studio Platform for managing projects, media, and clients.
            </Text>
            <Link
              href="https://www.youtube.com/@iland.studio"
              isExternal
              display="inline-flex"
              alignItems="center"
              gap={2.5}
              color="whiteAlpha.800"
              fontSize="sm"
              fontWeight="700"
              _hover={{ color: 'red.300', textDecoration: 'none' }}
            >
              <Box w="30px" h="30px" borderRadius="full" bg="red.500" color="white" display="grid" placeItems="center">
                <YouTubeMark />
              </Box>
              @iland.studio
            </Link>
          </VStack>

          <VStack align="flex-start" spacing={2} gridColumn={{ lg: 'span 3' }}>
            <Text fontSize="xs" fontWeight="800" letterSpacing="0.14em" textTransform="uppercase" color="whiteAlpha.500" mb={1}>
              Services
            </Text>
            {serviceLinks.map((service) => (
              <FooterLink key={service.label} to={service.to}>{service.label}</FooterLink>
            ))}
          </VStack>

          <VStack align="flex-start" spacing={2} gridColumn={{ lg: 'span 2' }}>
            <Text fontSize="xs" fontWeight="800" letterSpacing="0.14em" textTransform="uppercase" color="whiteAlpha.500" mb={1}>
              Explore
            </Text>
            {exploreLinks.map((item) => (
              <FooterLink key={item.label} to={item.to}>{item.label}</FooterLink>
            ))}
          </VStack>

          <VStack align="stretch" spacing={3} gridColumn={{ lg: 'span 3' }}>
            <Box>
              <Text fontSize="xs" fontWeight="800" letterSpacing="0.14em" textTransform="uppercase" color="whiteAlpha.500">
                Contact
              </Text>
              <Text color="whiteAlpha.700" fontSize="sm" mt={1}>Questions or bookings?</Text>
            </Box>
            <HStack p={3} border="1px solid" borderColor="whiteAlpha.200" borderRadius="lg" bg="whiteAlpha.50" spacing={3}>
              <EmailIcon color="teal.300" boxSize={4} />
              <Link href="mailto:contact@iland.studio" flex="1" minW={0} color="white" fontSize="sm" fontWeight="700" _hover={{ color: 'teal.200', textDecoration: 'none' }}>
                contact@iland.studio
              </Link>
              <IconButton
                aria-label="Copy contact email"
                icon={<CopyIcon />}
                size="sm"
                variant="ghost"
                color="whiteAlpha.600"
                onClick={copyEmail}
                _hover={{ color: 'teal.200', bg: 'whiteAlpha.100' }}
              />
            </HStack>
            <Button
              as={RouterLink}
              to="/contact"
              size="sm"
              colorScheme="teal"
              rightIcon={<ArrowForwardIcon />}
              alignSelf="flex-start"
              px={5}
            >
              Open contact form
            </Button>
          </VStack>
        </SimpleGrid>

        <Divider my={{ base: 6, md: 7 }} borderColor="whiteAlpha.200" />

        <Stack direction={{ base: 'column', md: 'row' }} justify="space-between" align={{ base: 'flex-start', md: 'center' }} spacing={3}>
          <Box>
            <Text fontSize="xs" color="whiteAlpha.600">© {new Date().getFullYear()} iLand Studio. All rights reserved.</Text>
            {buildDate && <Text fontSize="xs" color="whiteAlpha.400" mt={1}>Build updated {buildDate}</Text>}
          </Box>
          <Text fontSize="xs" color="whiteAlpha.500">
            Media services, business web apps, and the iLand Studio Platform — currently in development.
          </Text>
        </Stack>
      </Container>
    </Box>
  );
};

export default Footer;
